import { Add, Edit } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TableCell,
  TableRow,
  TableContainer,
  TablePagination,
  Typography,
  Table,
  TableHead,
  TableBody,
  Divider
} from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import { SortDirection } from 'localConstants/sortDirection'
import * as controllers from 'controllers'
import { BondPolicyNumberDto, SettingsBondPolicyDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { BondPolicyNumbersParameters } from 'parameters'
import { Fragment, useEffect, useState } from 'react'
import { formatCurrency, formatDateTime, useDebounce } from 'utils'
import BondPolicyAddEditDialog from './BondPolicyAddEditDialog'
import BondPolicyNumberAddEditDialog from './BondPolicyNumberAddEditDialog'
import { DEBOUNCE_DELAY } from 'localConstants'

interface BondPolicyListCardProps {
  handleRefresh: () => void
}

export default function BondPolicyListCard({ handleRefresh }: BondPolicyListCardProps) {
  // Bond Policy
  const [bondPolicy, setBondPolicy] = useState<SettingsBondPolicyDto>(new SettingsBondPolicyDto())
  const [bondPolicyAddEditDialogOpen, setBondPolicyAddEditDialogOpen] = useState<boolean>(false)
  const [isBondPolicySaving, setIsBondPolicySaving] = useState<boolean>(false)
  const [isGettingBondPolicy, setIsGettingBondPolicy] = useState<boolean>(false)

  // Bond Policy Numbers
  const [bondPolicyNumber, setBondPolicyNumber] = useState<BondPolicyNumberDto>(new BondPolicyNumberDto())
  const [bondPolicyNumberAddEditDialogOpen, setBondPolicyNumberAddEditDialogOpen] = useState<boolean>(false)
  const [bondPolicyNumbers, setBondPolicyNumbers] = useState<BondPolicyNumberDto[]>([])
  const [isGettingBondPolicyNumbers, setIsGettingBondPolicyNumbers] = useState<boolean>(false)
  const [isSavingBondPolicyNumber, setIsSavingBondPolicyNumber] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [parameters, setParameters] = useState<{
    page: number
    pageSize: number
    sortBy: string
    sortDirection: SortDirection

    start?: string
    end?: string
    createdBy?: string
    remaining?: string
  }>({
    page: 0,
    pageSize: 5,
    sortBy: 'start',
    sortDirection: 'desc'
  })

  useEffect(() => {
    setIsGettingBondPolicy(true)
    controllers
      .getBondPolicy()
      .then(res => {
        setBondPolicy(res.value)
      })
      .finally(() => {
        setIsGettingBondPolicy(false)
      })
  }, [])

  // TODO: Implement custom useDebouncedEffect hook
  useEffect(() => {
    getBondPolicyNumbers(parameters)
  }, [parameters])

  const getBondPolicyNumbers = useDebounce((parameters: BondPolicyNumbersParameters) => {
    setIsGettingBondPolicyNumbers(true)
    controllers
      .getBondPolicyNumbers(parameters)
      .then(res => {
        setBondPolicyNumbers(res.value)
        setTotalCount(res.totalCount ?? 0)
      })
      .finally(() => {
        setIsGettingBondPolicyNumbers(false)
      })
  }, DEBOUNCE_DELAY)

  return (
    <>
      <BondPolicyAddEditDialog
        initialValues={bondPolicy}
        isLoading={isBondPolicySaving}
        onClose={() => {
          setBondPolicyAddEditDialogOpen(false)
        }}
        onSave={values => {
          setIsBondPolicySaving(true)
          controllers
            .updateBondPolicy(values)
            .then(res => {
              setBondPolicyAddEditDialogOpen(false)
              enqueueSnackbar('Bond Policy Saved Successfully!', { variant: 'success' })
              setBondPolicy(res.value)
            })
            .finally(() => {
              setIsBondPolicySaving(false)
            })
        }}
        open={bondPolicyAddEditDialogOpen}
      />

      <BondPolicyNumberAddEditDialog
        initialValues={bondPolicyNumber}
        isLoading={isSavingBondPolicyNumber}
        onClose={() => {
          setBondPolicyNumberAddEditDialogOpen(false)
        }}
        onSave={values => {
          const requestMethod = values.bondPolicyNumberGuid
            ? controllers.updateBondPolicyNumber
            : controllers.createBondPolicyNumber

          setIsSavingBondPolicyNumber(true)
          requestMethod(values)
            .then(res => {
              setBondPolicyNumberAddEditDialogOpen(false)
              enqueueSnackbar('Bond Policy Number Saved Successfully!', { variant: 'success' })
              // getBondPolicyNumbers(parameters)
              handleRefresh()
            })
            .finally(() => {
              setIsSavingBondPolicyNumber(false)
            })
        }}
        open={bondPolicyNumberAddEditDialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Bond Policy
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  onClick={() => {
                    setIsBondPolicySaving(false)
                    setBondPolicyAddEditDialogOpen(true)
                  }}
                  variant='text'
                  endIcon={<Edit />}
                >
                  EDIT
                </Button>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {isGettingBondPolicy && (
            <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
              <Grid>
                <CircularProgress color='primary' size={50} />
              </Grid>
            </Grid>
          )}

          {!isGettingBondPolicy && (
            <>
              <Grid item xs={12}>
                <LabelField
                  label='Bond Threshold'
                  value={bondPolicy.bondThreshold ? formatCurrency(bondPolicy.bondThreshold) : ''}
                />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Next Policy #' value={bondPolicy.nextPolicyNumber} />
              </Grid>

              <Grid item xs={12}>
                <LabelField
                  label='Bond Block Quantity Alert Notification Email'
                  value={bondPolicy.bondBlockQuantityAlertNotificationEmail}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} container spacing={1} alignItems='center' sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <Card variant='outlined'>
                <CardContent>
                  <Grid container spacing={1} alignItems='flexStart'>
                    <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
                      <Grid item xs={12} sm='auto'>
                        <Typography variant='h6'>Bond Policy Number</Typography>
                      </Grid>

                      <Grid item xs={12} sm='auto'>
                        <Button
                          color='primary'
                          endIcon={<Add />}
                          onClick={() => {
                            setBondPolicyNumber(new BondPolicyNumberDto())
                            setBondPolicyNumberAddEditDialogOpen(true)
                          }}
                          variant='text'
                        >
                          ADD
                        </Button>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider />
                    </Grid>

                    {isGettingBondPolicyNumbers && (
                      <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                        <Grid>
                          <CircularProgress color='primary' size={50} />
                        </Grid>
                      </Grid>
                    )}

                    {!isGettingBondPolicyNumbers && (
                      <Grid item xs={12}>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Start</TableCell>

                                <TableCell sx={theme => ({ color: theme.palette.primary.main })}>End</TableCell>

                                <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Created By</TableCell>

                                <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Remaining</TableCell>

                                <TableCell></TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {bondPolicyNumbers.map(bondPolicyNumber => (
                                <TableRow id={bondPolicyNumber.bondPolicyNumberGuid}>
                                  <TableCell>{bondPolicyNumber.blockStart}</TableCell>

                                  <TableCell>{bondPolicyNumber.blockEnd}</TableCell>

                                  <TableCell>
                                    <Typography>
                                      {bondPolicyNumber.createdBy?.firstName} {bondPolicyNumber.createdBy?.lastName}
                                    </Typography>
                                    <Typography>{formatDateTime(bondPolicyNumber.createdDateTime!)}</Typography>
                                  </TableCell>

                                  <TableCell>{bondPolicyNumber.remainingQuantity}</TableCell>

                                  <TableCell padding='checkbox'>
                                    <Button
                                      color='primary'
                                      endIcon={<Edit />}
                                      onClick={() => {
                                        setBondPolicyNumber(bondPolicyNumber)
                                        setBondPolicyNumberAddEditDialogOpen(true)
                                      }}
                                      variant='text'
                                    >
                                      EDIT
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>

                <TablePagination
                  component='div'
                  count={totalCount}
                  onPageChange={(_, page: number) => {
                    setParameters({
                      ...parameters,
                      page
                    })
                  }}
                  onRowsPerPageChange={e => {
                    setParameters({
                      ...parameters,
                      page: 0,
                      pageSize: e.target.value as unknown as number
                    })
                  }}
                  page={parameters.page}
                  rowsPerPage={parameters.pageSize}
                  labelRowsPerPage='Rows per page:'
                  rowsPerPageOptions={[
                    { label: '5', value: 5 },
                    { label: '10', value: 10 },
                    { label: '25', value: 25 },
                    { label: '50', value: 50 },
                    { label: '100', value: 100 }
                  ]}
                  SelectProps={{ fullWidth: false }}
                />
              </Card>
            </Grid>

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                ||Click add to add a new record.|| Click edit to edit an existing record.||Bond Policy Numbers are used on the
                Bond document during the importation process as well as the Recall Warranty document during the Conformity
                process.||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
